import { useCallback, useState } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import ShowroomTileCarousel from '@/components/showrooms/ShowroomTileCarousel';

const scrimLayers = (uniform, radial, bottom, top) =>
  [
    `linear-gradient(rgba(0, 0, 0, ${uniform}), rgba(0, 0, 0, ${uniform}))`,
    `radial-gradient(ellipse 72% 58% at 50% 46%, rgba(0, 0, 0, ${radial}) 0%, rgba(0, 0, 0, ${radial * 0.43}) 58%, transparent 100%)`,
    `linear-gradient(to bottom, rgba(0, 0, 0, ${bottom}) 0%, transparent 48%)`,
    `linear-gradient(to top, rgba(0, 0, 0, ${top}) 0%, transparent 42%)`,
  ].join(', ');

const TILE_SCRIM_DEFAULT = scrimLayers(0.36, 0.32, 0.26, 0.24);
const TILE_SCRIM_HOVER = scrimLayers(0.3, 0.26, 0.22, 0.2);
const scrimTransition = 'opacity 0.55s ease';

const tileTextShadow = ['0 1px 4px rgba(0, 0, 0, 0.65)', '0 2px 12px rgba(0, 0, 0, 0.5)'].join(
  ', ',
);

const collectionCtaSx = {
  borderRadius: 0,
  letterSpacing: '0.2em',
  fontSize: 11,
  minWidth: 200,
  mt: 2.5,
  py: 1.5,
  color: 'common.white',
  borderColor: 'rgba(255,255,255,0.65)',
  opacity: 0,
  transform: 'translateY(8px)',
  transition:
    'opacity 0.3s ease, transform 0.3s ease, border-color 0.2s ease, background-color 0.2s ease',
  pointerEvents: 'none',
  '&:hover': {
    borderColor: 'common.white',
    bgcolor: 'rgba(255,255,255,0.08)',
  },
};

export default function PortfolioCollectionTile({
  collection,
  isActive = false,
  onActivate,
  onDeactivate,
}) {
  const [localHovered, setLocalHovered] = useState(false);
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const { href } = collection;
  const hovered = canHover ? localHovered : isActive;
  const isTouchTile = Boolean(href && !canHover);
  const titleLabel = collection.title.charAt(0) + collection.title.slice(1).toLowerCase();

  const setActive = useCallback(
    active => {
      if (canHover) {
        setLocalHovered(active);
      } else if (active) {
        onActivate?.();
      } else {
        onDeactivate?.();
      }
    },
    [canHover, onActivate, onDeactivate],
  );

  const handleTouchTileClick = useCallback(() => {
    if (!isTouchTile) return;
    if (isActive) {
      onDeactivate?.();
    } else {
      onActivate?.();
    }
  }, [isTouchTile, isActive, onActivate, onDeactivate]);

  const handleTouchKeyDown = useCallback(
    e => {
      if (!isTouchTile) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleTouchTileClick();
      }
    },
    [isTouchTile, handleTouchTileClick],
  );

  const handleCtaClick = useCallback(e => {
    e.stopPropagation();
  }, []);

  const rootIsLink = Boolean(href && canHover);
  const rootComponent = rootIsLink ? Link : 'article';
  let rootAriaLabel;
  if (rootIsLink) {
    rootAriaLabel = `View ${titleLabel} collection`;
  } else if (isTouchTile) {
    rootAriaLabel = `Preview ${titleLabel} collection`;
  }
  const tabIndex = isTouchTile || !rootIsLink ? 0 : undefined;

  return (
    <Box
      component={rootComponent}
      href={rootIsLink ? href : undefined}
      role={isTouchTile ? 'button' : undefined}
      tabIndex={tabIndex}
      aria-label={rootAriaLabel}
      aria-expanded={isTouchTile ? isActive : undefined}
      className={`showroom-tile${hovered ? ' showroom-tile--active' : ''}`}
      onClick={isTouchTile ? handleTouchTileClick : undefined}
      onKeyDown={isTouchTile ? handleTouchKeyDown : undefined}
      onMouseEnter={canHover ? () => setActive(true) : undefined}
      onMouseLeave={canHover ? () => setActive(false) : undefined}
      onFocus={canHover ? () => setActive(true) : undefined}
      onBlur={
        canHover
          ? e => {
              if (!e.currentTarget.contains(e.relatedTarget)) setActive(false);
            }
          : undefined
      }
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1 / 1',
        height: 'auto',
        minHeight: 0,
        overflow: 'hidden',
        outline: 'none',
        cursor: href ? 'pointer' : 'default',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <Box className="showroom-tile-media" sx={{ position: 'absolute', inset: 0 }} aria-hidden>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className="showroom-tile-exterior"
          src={collection.coverImage}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: hovered ? 0 : 1,
            transition: 'opacity 0.55s ease',
          }}
        />
        {hovered ? <ShowroomTileCarousel slides={collection.hoverSlides} /> : null}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: TILE_SCRIM_DEFAULT,
              opacity: hovered ? 0 : 1,
              transition: scrimTransition,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: TILE_SCRIM_HOVER,
              opacity: hovered ? 1 : 0,
              transition: scrimTransition,
            }}
          />
        </Box>
      </Box>

      <Box
        className="showroom-tile-frame"
        sx={{
          position: 'absolute',
          inset: { xs: 12, md: 16 },
          border: '1px solid',
          borderColor: 'common.white',
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
          textAlign: 'center',
          color: 'common.white',
          textTransform: 'uppercase',
          textShadow: tileTextShadow,
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontWeight: 600,
            letterSpacing: '0.12em',
            fontSize: { xs: '1.75rem', md: '2.25rem', lg: '2.5rem' },
            lineHeight: 1.15,
            mb: 1.5,
          }}
        >
          {collection.title}
        </Typography>
        <Typography
          component="p"
          sx={{
            fontWeight: 500,
            letterSpacing: '0.18em',
            fontSize: { xs: 10, md: 11 },
            lineHeight: 1.6,
            maxWidth: 320,
          }}
        >
          {collection.name}
        </Typography>
        {collection.tagline ? (
          <Typography
            component="p"
            sx={{
              fontWeight: 400,
              letterSpacing: '0.14em',
              fontSize: { xs: 9, md: 10 },
              lineHeight: 1.6,
              mt: 0.75,
              opacity: 0.95,
              maxWidth: 320,
            }}
          >
            {collection.tagline}
          </Typography>
        ) : null}
        {isTouchTile ? (
          <Button
            component={Link}
            href={href}
            variant="outlined"
            tabIndex={hovered ? 0 : -1}
            aria-hidden={!hovered}
            aria-label={`View ${titleLabel} collection`}
            onClick={handleCtaClick}
            sx={{
              ...collectionCtaSx,
              ...(hovered
                ? {
                    opacity: 1,
                    transform: 'translateY(0)',
                    pointerEvents: 'auto',
                  }
                : {}),
            }}
          >
            View Collection
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

PortfolioCollectionTile.propTypes = {
  collection: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    tagline: PropTypes.string,
    href: PropTypes.string.isRequired,
    coverImage: PropTypes.string.isRequired,
    hoverSlides: PropTypes.array.isRequired,
  }).isRequired,
  isActive: PropTypes.bool,
  onActivate: PropTypes.func,
  onDeactivate: PropTypes.func,
};
